import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { GameClient } from '../game/GameClient';
import { audio } from '../game/audio';
import { getSettings, updateSettings } from '../game/settings';
import { onLangChange, t } from '../i18n';
import { ClassTree } from './ClassTree';
import { Hud } from './Hud';
import { HelpPanel, Modal, SettingsPanel } from './Panels';
import { FriendsPage, ProfilePage, useMe } from './AccountPanel';
import { OrderWatcher, ShopPage } from './ShopPanel';
import { PassPage } from './PassPage';
import { Hangar } from './Hangar';
import { type ModeChoice } from './ModePicker';
import { GroupDock, groupLimit, useGroup } from './GroupPanel';
import { MenuShell, Sheet, pageFromHash, type Page } from './MenuShell';
import { apiPost } from '../game/api';

type Overlay = null | 'settings' | 'tree' | 'help';

function useLangRerender(): void {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => onLangChange(force), []);
}

export function App() {
  useLangRerender();
  const [screen, setScreen] = useState<'menu' | 'game'>('menu');
  const [page, setPage] = useState<Page>(pageFromHash);
  const [name, setName] = useState(getSettings().name);
  const [overlay, setOverlay] = useState<Overlay>(null);
  const { me, reload } = useMe();
  const [notice, setNotice] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [modeChoice, setModeChoice] = useState<ModeChoice>({ mode: 'ffa' });
  // Partie en cours : mode envoyé au serveur (avec le drapeau « groupe ») et clé de remontage.
  const [game, setGame] = useState<{ mode: ModeChoice & { group?: boolean }; key: number }>({ mode: { mode: 'ffa' }, key: 0 });
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;
  const myPublicId = me?.account?.publicId ?? null;

  // Navigation entre les pages du menu, reprise par le bouton Retour du navigateur.
  const pageRef = useRef(page);
  pageRef.current = page;
  const go = useCallback((p: Page) => {
    if (pageRef.current === p) return;
    history.pushState(null, '', p === 'hangar' ? location.pathname + location.search : `#${p}`);
    setPage(p);
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    // Entrée « partie » restée dans l'historique après un rechargement : elle redevient une entrée de menu.
    if ((history.state as { game?: boolean } | null)?.game) history.replaceState(null, '');
    const pop = () => {
      // Avant (navigateur) vers une ancienne entrée de partie alors qu'on est au menu : on l'annule.
      if (screenRef.current !== 'game' && (history.state as { game?: boolean } | null)?.game) {
        history.back();
        return;
      }
      // En partie, Retour (bouton du téléphone ou du navigateur) ferme d'abord une feuille ouverte, sinon revient au
      // hangar, au lieu de quitter le site.
      if (screenRef.current === 'game') {
        if (overlayRef.current) {
          setOverlay(null);
          history.pushState({ game: true }, '');
        } else quitRef.current(undefined, true);
      }
      setPage(pageFromHash());
    };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);
  // Les sons d'interface ne peuvent démarrer qu'après un geste du joueur.
  useEffect(() => {
    const unlock = () => audio.unlock();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const startGame = (mode: ModeChoice & { group?: boolean }) => {
    updateSettings({ name });
    setNotice('');
    // Entrée d'historique propre à la partie : Retour revient au hangar au lieu de quitter le site.
    // Un nouveau lancement depuis la partie (groupe) garde la même entrée.
    if (screenRef.current !== 'game') history.pushState({ game: true }, '');
    screenRef.current = 'game';
    setGame((g) => ({ mode, key: g.key + 1 }));
    setScreen('game');
  };
  /** Retour au hangar : bouton du HUD, fin de match ou erreur (raison affichée), ou bouton Retour (viaHistory). */
  const quitGame = (reason?: string, viaHistory = false) => {
    screenRef.current = 'menu';
    setScreen('menu');
    setOverlay(null);
    if (typeof reason === 'string' && reason) setNotice(t(`leave_${reason}` as 'slogan'));
    void reload();
    // L'entrée d'historique de la partie est retirée : sinon le Retour suivant semblerait ne rien faire.
    if (!viaHistory && (history.state as { game?: boolean } | null)?.game) history.back();
  };
  const quitRef = useRef(quitGame);
  quitRef.current = quitGame;
  // Un membre resté au menu suit automatiquement le lancement du chef.
  const [joinedLaunch, setJoinedLaunch] = useState(0);
  const group = useGroup(!!me?.account, myPublicId, (req, seq) => {
    if (screenRef.current !== 'menu') return;
    setJoinedLaunch(seq);
    startGame({ ...req, group: true });
  });
  const g = group.state.group;
  const groupSize = g?.members.length ?? 0;
  const isLeader = !!g && g.leader === myPublicId;
  const tooBig = isLeader && groupSize > groupLimit(modeChoice);

  // Liens reçus par e-mail : vérification et réinitialisation (jetons à usage unique).
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const verify = q.get('verify');
    const reset = q.get('reset');
    const order = q.get('order');
    if (verify || reset || order) history.replaceState(null, '', location.pathname);
    if (order && /^[0-9a-f-]{36}$/.test(order)) {
      setNotice(q.get('cancel') ? t('orderCancelled') : t('orderChecking'));
      if (!q.get('cancel')) setOrderId(order);
    }
    if (verify)
      void apiPost<{ ok: boolean }>('/api/verify', { token: verify }).then((r) => {
        setNotice(r.data.ok ? t('verifyOk') : t('verifyFail'));
        void reload();
      });
    if (reset) setResetToken(reset);
  }, [reload]);

  const play = () => {
    if (tooBig) {
      setNotice(t('groupTooBig', { n: groupSize, max: groupLimit(modeChoice) }));
      return;
    }
    startGame(isLeader && groupSize > 1 ? { ...modeChoice, group: true } : modeChoice);
  };

  const pageView = (): ReactNode => {
    switch (page) {
      case 'tree':
        return <ClassTree loggedIn={!!me?.account} />;
      case 'pass':
        return <PassPage me={me} onChanged={() => void reload()} go={go} />;
      case 'shop':
        return <ShopPage me={me} onChanged={() => void reload()} go={go} />;
      case 'friends':
        return <FriendsPage me={me} group={group} go={go} />;
      case 'profile':
        return <ProfilePage me={me} onChanged={() => void reload()} />;
      case 'settings':
        return <SettingsPanel />;
      case 'help':
        return <HelpPanel />;
      default:
        return (
          <Hangar
            me={me}
            name={name}
            onName={setName}
            mode={modeChoice}
            onMode={setModeChoice}
            onPlay={play}
            notice={notice}
            onNotice={setNotice}
            group={group}
            tooBig={tooBig ? { n: groupSize, max: groupLimit(modeChoice) } : null}
            followsLeader={!!g && !isLeader && groupSize > 1}
            go={go}
          />
        );
    }
  };

  return (
    <>
      {screen === 'menu' ? (
        <MenuShell page={page} go={go} me={me} badges={{ friends: group.state.invites.length }}>
          {pageView()}
        </MenuShell>
      ) : (
        <GameScreen
          key={game.key}
          name={name}
          mode={game.mode}
          dock={
            me?.account ? (
              <GroupDock
                me={me.account}
                group={group}
                joinedSeq={joinedLaunch}
                onJoinLaunch={(req, seq) => {
                  setJoinedLaunch(seq);
                  startGame({ ...req, group: true });
                }}
              />
            ) : null
          }
          onOverlay={setOverlay}
          onQuit={(reason) => quitGame(reason)}
        />
      )}
      {screen === 'game' && overlay === 'settings' && (
        <Sheet title={t('settings')} onClose={() => setOverlay(null)}>
          <SettingsPanel bare />
        </Sheet>
      )}
      {screen === 'game' && overlay === 'tree' && (
        <Sheet title={t('classTree')} onClose={() => setOverlay(null)}>
          <ClassTree bare loggedIn={!!me?.account} />
        </Sheet>
      )}
      {screen === 'game' && overlay === 'help' && (
        <Sheet title={t('help')} onClose={() => setOverlay(null)}>
          <HelpPanel bare />
        </Sheet>
      )}
      {orderId && (
        <OrderWatcher
          orderId={orderId}
          onDone={(text) => {
            setOrderId(null);
            setNotice(text);
            void reload();
          }}
        />
      )}
      {resetToken && (
        <ResetForm
          token={resetToken}
          onDone={(ok) => {
            setResetToken(null);
            setNotice(ok ? t('resetOk') : t('resetFail'));
          }}
        />
      )}
    </>
  );
}

function GameScreen({
  name,
  mode,
  dock,
  onOverlay,
  onQuit,
}: {
  name: string;
  mode: ModeChoice;
  dock: ReactNode;
  onOverlay: (o: Overlay) => void;
  onQuit: (reason?: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [client, setClient] = useState<GameClient | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    const c = new GameClient(ref.current, name.trim() || 'Invité', mode as unknown as Record<string, unknown>, (reason) => onQuit(reason));
    let cancelled = false;
    void c.start().then(() => {
      if (!cancelled) setClient(c);
    });
    document.body.classList.add('in-game');
    return () => {
      cancelled = true;
      c.stop();
      document.body.classList.remove('in-game');
    };
  }, [name]);
  return (
    <div className="game" ref={ref}>
      {client && <HudBinding client={client} onOverlay={onOverlay} onQuit={onQuit} />}
      {client && dock}
    </div>
  );
}

function HudBinding({ client, onOverlay, onQuit }: { client: GameClient; onOverlay: (o: Overlay) => void; onQuit: (reason?: string) => void }) {
  const state = useSyncExternalStore(client.hud.subscribe, client.hud.get);
  return <Hud state={state} client={client} onOverlay={onOverlay} onQuit={onQuit} />;
}

function ResetForm({ token, onDone }: { token: string; onDone: (ok: boolean) => void }) {
  const [password, setPassword] = useState('');
  return (
    <Modal title={t('resetTitle')} onClose={() => onDone(false)}>
      <form
        className="auth-form"
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await apiPost<{ ok: boolean }>('/api/reset', { token, password });
          onDone(!!r.data.ok);
        }}
      >
        <input type="password" required minLength={10} autoComplete="new-password" placeholder={t('password')} value={password} onChange={(e) => setPassword(e.target.value)} />
        <button className="btn btn-primary" type="submit">
          {t('resetTitle')}
        </button>
      </form>
    </Modal>
  );
}
